import { motion } from "framer-motion";
import { Fragment, useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Minus, Sparkles, Building2, User, Users, Briefcase, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRegion } from "../context/RegionContext";
import { usePlans, fmtInt, fmtExpiration, fmtHistory, fmtAmountMinor } from "../lib/usePlans";

// Montants dérivés de l'API, compatibles catalogue commercial (minor units) ET legacy (EUR).
const planCurrency = (p) => p.currency || "EUR";
const planDecimals = (p) => (p.currency_decimals != null ? p.currency_decimals : 2);
const baseMinor = (p) =>
  p.base_price_minor != null ? p.base_price_minor : (p.price_eur != null ? Math.round(p.price_eur * 100) : null);
const addMinor = (p) =>
  p.additional_member_price_minor != null
    ? p.additional_member_price_minor
    : (p.additional_member_price_eur != null ? Math.round(p.additional_member_price_eur * 100) : null);
const fmtPlanMinor = (p, minor) => fmtAmountMinor(minor, planCurrency(p), planDecimals(p));

const SOON = "soon";
const YES = "yes";
const NO = "no";

// Icône + flag highlight + plage du calculateur : seules métadonnées NON textuelles conservées en dur.
// Tous les libellés (tagline, cta, badge, footnote, extras) viennent des traductions (ns public).
const EDITORIAL_META = {
  free: { icon: User, highlight: false },
  pro: { icon: Sparkles, highlight: false },
  team: { icon: Users, highlight: true, calculator: { min: 5, max: 30 } },
  business: { icon: Briefcase, highlight: false, calculator: { min: 20, max: 45 } },
  enterprise: { icon: Building2, highlight: false },
};

// Normalise les extras issus du JSON : chaîne -> chaîne ; { label, soon } -> [label, SOON].
const normExtras = (arr) => (Array.isArray(arr) ? arr : []).map((x) => (typeof x === "string" ? x : [x.label, SOON]));

// Prix d'affichage dérivé de l'API (devise régionale).
const priceLabel = (p, t) => (p.requires_quote ? t("pricing.quote") : (fmtPlanMinor(p, baseMinor(p)) || "—"));
const periodLabel = (p, t) => (p.requires_quote ? "" : t("pricing.perMonth"));

// Sous-titre tarifaire (membres inclus + tarif additionnel) dérivé de l'API.
const subpriceLabel = (p, t) => {
  const add = addMinor(p);
  if (p.included_members == null || add == null) return null;
  const base = t("pricing.sub.included", { count: p.included_members });
  const extra = t("pricing.sub.extra", { price: fmtPlanMinor(p, add) });
  if (p.key === "business" && p.self_service_max_members != null) {
    return `${base} · ${extra} · ${t("pricing.sub.online", { count: p.self_service_max_members })}`;
  }
  return `${base} · ${extra}`;
};

// Construit la liste des fonctionnalités affichées à partir des données commerciales de l'API.
const buildFeatures = (p, extras, t) => {
  const f = [];
  f.push(p.monthly_secret_soft_limit == null ? t("pricing.feat.customVolumes") : t("pricing.feat.secretsPerMonth", { count: fmtInt(p.monthly_secret_soft_limit) }));
  if (p.included_members == null) f.push(t("pricing.feat.customUsers"));
  else if (p.included_members === 1) f.push(t("pricing.feat.oneUser"));
  else f.push(t("pricing.feat.includedUsers", { count: p.included_members }));
  if (addMinor(p) != null) f.push(t("pricing.feat.additionalUsers", { price: fmtPlanMinor(p, addMinor(p)) }));
  f.push(t("pricing.feat.expirationUpTo", { dur: fmtExpiration(p.max_expiration) }));
  if (p.max_custom_domains == null) f.push(t("pricing.feat.customDomains"));
  else if (p.max_custom_domains > 0) f.push(t("pricing.feat.customDomainsN", { count: p.max_custom_domains }));
  if (p.capabilities.can_use_branding) f.push(t("pricing.feat.branding"));
  // Aucune API publique documentée/supportée à ce jour : affichée « Bientôt » si prévue par l'offre.
  if (p.capabilities.can_use_api) f.push([t("pricing.feat.api"), SOON]);
  if (p.monthly_secure_request_limit != null)
    f.push(t("pricing.feat.secureRequestsN", { count: fmtInt(p.monthly_secure_request_limit) }));
  else f.push(t("pricing.feat.secureRequests"));
  f.push(t("pricing.feat.history", { retention: fmtHistory(p.history_retention_days) }));
  if (p.commercial_includes.sms_delivery) f.push(t("pricing.feat.smsIncluded"));
  if (p.commercial_includes.recipient_otp)
    f.push(p.capabilities.can_use_recipient_otp ? t("pricing.feat.recipientOtp") : [t("pricing.feat.recipientOtp"), SOON]);
  if (p.commercial_includes.webhooks) f.push(p.capabilities.can_use_webhooks ? t("pricing.feat.webhooks") : [t("pricing.feat.webhooks"), SOON]);
  if (p.commercial_includes.sso) f.push(p.capabilities.can_use_sso ? t("pricing.feat.sso") : [t("pricing.feat.sso"), SOON]);
  return f.concat(extras);
};

const SoonPill = () => {
  const { t } = useTranslation("public");
  return (
    <span
      data-testid="soon-pill"
      className="ml-1.5 inline-flex items-center rounded-full bg-amber-100 border border-amber-300 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700"
    >
      {t("pricing.soonBadge")}
    </span>
  );
};

// Calculateur tarifaire interactif (curseur d'utilisateurs -> /api/pricing/preview en direct).
const PriceCalculator = ({ plan, min, max, included, highlight, region }) => {
  const { t } = useTranslation("public");
  const [members, setMembers] = useState(included);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const fetchPrice = useCallback(
    (n) => {
      setLoading(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(async () => {
        try {
          const q = `/pricing/preview?plan=${plan}&members=${n}${region ? `&region=${encodeURIComponent(region)}` : ""}`;
          const { data } = await api.get(q);
          setResult(data);
        } catch (e) {
          setResult(null);
        } finally {
          setLoading(false);
        }
      }, 180);
    },
    [plan, region]
  );

  useEffect(() => {
    fetchPrice(members);
    return () => clearTimeout(timer.current);
  }, [fetchPrice, members]);

  const onChange = (v) => {
    const n = Array.isArray(v) ? v[0] : v;
    setMembers(n);
    fetchPrice(n);
  };

  const quote = result?.requires_quote;
  let priceStr = null;
  if (result) {
    if (result.monthly_price_minor != null)
      priceStr = fmtAmountMinor(result.monthly_price_minor, result.currency || "EUR", result.currency_decimals);
    else if (result.monthly_price_eur != null)
      priceStr = fmtAmountMinor(Math.round(result.monthly_price_eur * 100), "EUR", 2);
  }

  return (
    <div
      data-testid={`price-calculator-${plan}`}
      className={`rounded-xl p-4 mb-5 border ${
        highlight ? "bg-emerald-500/[0.06] border-emerald-500/30" : "bg-slate-50 border-slate-200"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-slate-600">{t("pricing.estimate")}</span>
        <span
          data-testid={`calc-members-${plan}`}
          className="text-xs font-mono-token font-semibold text-slate-900"
        >
          {t("pricing.usersSuffix", { count: members })}
        </span>
      </div>
      <Slider
        data-testid={`calc-slider-${plan}`}
        value={[members]}
        min={min}
        max={max}
        step={1}
        onValueChange={onChange}
        className="my-3"
      />
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] text-slate-400">{t("pricing.minSuffix", { count: min })}</span>
        <div className="text-right" data-testid={`calc-result-${plan}`}>
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin text-slate-400 inline" />
          ) : quote ? (
            <span className="font-heading text-lg font-extrabold text-emerald-600">{t("pricing.quote")}</span>
          ) : (
            <span className="font-heading">
              <span className="text-2xl font-extrabold text-slate-900">{priceStr}</span>
              <span className="text-xs text-slate-400 font-medium">{t("pricing.perMonth")}</span>
            </span>
          )}
        </div>
      </div>
      {quote && (
        <p className="text-[11px] text-slate-500 mt-1 text-right">
          {t("pricing.quoteNote", { count: plan === "business" ? 40 : max })}
        </p>
      )}
    </div>
  );
};

const Feature = ({ item }) => {
  const soon = Array.isArray(item) && item[1] === SOON;
  const label = Array.isArray(item) ? item[0] : item;
  return (
    <li className={`flex items-start gap-2 text-sm ${soon ? "text-slate-400" : "text-slate-600"}`}>
      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${soon ? "text-slate-300" : "text-emerald-600"}`} />
      <span>
        {label}
        {soon && <SoonPill />}
      </span>
    </li>
  );
};

const capCell = (included, canUse) => (included ? (canUse ? YES : SOON) : NO);

// Construit le comparatif détaillé : lignes commerciales dérivées de l'API,
// lignes purement éditoriales conservées telles quelles (garantie ZK, support, audit…).
const buildComparison = (plans, t) => {
  const map = (fn) => plans.map(fn);
  const r = (k) => t(`pricing.cmp.rows.${k}`);
  const v = (k, opts) => t(`pricing.cmp.val.${k}`, opts);
  return [
    {
      category: t("pricing.cmp.categories.security"),
      rows: [
        { label: r("zeroKnowledge"), values: plans.map(() => YES) },
        { label: r("readOnce"), values: plans.map(() => YES) },
        { label: r("maxExpiration"), values: map((p) => fmtExpiration(p.max_expiration)) },
        {
          label: r("recipientVerification"),
          values: map((p) => capCell(p.commercial_includes.recipient_otp, p.capabilities.can_use_recipient_otp)),
        },
        // Politique de mot de passe du Workspace : réservée aux offres payantes (backend).
        { label: r("securityPolicies"), values: map((p) => (p.key === "free" ? NO : YES)) },
      ],
    },
    {
      category: t("pricing.cmp.categories.collaboration"),
      rows: [
        {
          label: r("users"),
          values: map((p) =>
            p.included_members == null ? v("custom") : p.included_members === 1 ? "1" : v("includedSuffix", { count: p.included_members })
          ),
        },
        { label: r("memberManagement"), values: map((p) => (p.included_members == null || p.included_members > 1 ? YES : NO)) },
        { label: r("teamRoles"), values: map((p) => (p.included_members == null || p.included_members > 1 ? YES : NO)) },
        {
          label: r("secureRequests"),
          values: map((p) =>
            p.monthly_secure_request_limit != null
              ? v("perMonth", { count: p.monthly_secure_request_limit })
              : p.capabilities.can_use_secure_requests
              ? YES
              : NO
          ),
        },
        { label: r("smsDelivery"), values: map((p) => (p.commercial_includes.sms_delivery ? YES : NO)) },
        { label: r("auditLog"), values: map((p) => fmtHistory(p.history_retention_days)) },
        { label: r("directory"), values: plans.map(() => YES) },
      ],
    },
    {
      category: t("pricing.cmp.categories.customization"),
      rows: [
        { label: r("branding"), values: map((p) => (p.capabilities.can_use_branding ? YES : NO)) },
        {
          label: r("customDomains"),
          values: map((p) => (p.max_custom_domains == null ? v("custom") : String(p.max_custom_domains))),
        },
      ],
    },
    {
      category: t("pricing.cmp.categories.integration"),
      rows: [
        { label: r("api"), values: map((p) => (p.capabilities.can_use_api ? SOON : NO)) },
        { label: r("webhooks"), values: map((p) => capCell(p.commercial_includes.webhooks, p.capabilities.can_use_webhooks)) },
        { label: r("sso"), values: map((p) => capCell(p.commercial_includes.sso, p.capabilities.can_use_sso)) },
        { label: r("scim"), values: [NO, NO, NO, NO, SOON] },
      ],
    },
    {
      category: t("pricing.cmp.categories.support"),
      rows: [
        { label: r("supportLevel"), values: [v("standard"), v("email"), v("priority"), v("priority"), v("dedicatedSla")] },
      ],
    },
  ];
};

const Cell = ({ value }) => {
  const { t } = useTranslation("public");
  if (value === YES) return <Check className="w-4 h-4 text-emerald-600 mx-auto" data-testid="cmp-yes" />;
  if (value === NO) return <Minus className="w-4 h-4 text-slate-300 mx-auto" data-testid="cmp-no" />;
  if (value === SOON)
    return (
      <span className="inline-flex items-center rounded-full bg-amber-100 border border-amber-300 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
        {t("pricing.soonBadge")}
      </span>
    );
  return <span className="text-sm text-slate-700">{value}</span>;
};

export const PricingAndCustomOffers = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("public");
  const { user } = useAuth();
  // Région pilotée par le sélecteur GLOBAL du header (source de vérité unique).
  const { region } = useRegion();
  const { plans, data, loading, error } = usePlans(region);
  const activeRegion = region || data?.region || null;
  const carouselRef = useRef(null);

  const [activeIndex, setActiveIndex] = useState(1);

  // Le carrousel démarre sur l'offre Pro (2e carte).
  useEffect(() => {
    const el = carouselRef.current;
    if (el && el.children[1]) {
      el.scrollLeft = el.children[1].offsetLeft - el.offsetLeft;
    }
  }, [plans]);

  const scrollCarousel = (dir) => {
    const el = carouselRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 520, behavior: "smooth" });
  };

  const goToCard = (i) => {
    const el = carouselRef.current;
    if (!el || !el.children[i]) return;
    setActiveIndex(i);
    el.scrollTo({ left: el.children[i].offsetLeft - el.offsetLeft, behavior: "smooth" });
  };

  const handleCarouselScroll = () => {
    const el = carouselRef.current;
    if (!el) return;
    if (el.scrollLeft <= 4) {
      setActiveIndex(0);
      return;
    }
    if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 4) {
      setActiveIndex(el.children.length - 1);
      return;
    }
    const target = el.scrollLeft;
    let best = 0;
    let bestDist = Infinity;
    Array.from(el.children).forEach((c, i) => {
      const d = Math.abs(c.offsetLeft - el.offsetLeft - target);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    setActiveIndex(best);
  };

  const choosePlan = (key) => {
    if (key === "free") {
      navigate("/");
      return;
    }
    if (key === "enterprise") {
      toast.success(t("pricing.enterpriseToast"));
      return;
    }
    if (user) navigate("/compte");
    else navigate("/connexion?mode=register");
  };

  if (loading) {
    return (
      <section data-testid="pricing-section" className="py-20 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
      </section>
    );
  }

  if (error || !plans || plans.length === 0) {
    return (
      <section data-testid="pricing-section" className="py-20 text-center text-slate-500">
        {t("pricing.loadError")}
      </section>
    );
  }

  const comparison = buildComparison(plans, t);

  return (
    <section data-testid="pricing-section" className="py-4">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <span className="text-xs font-semibold tracking-[0.18em] uppercase text-emerald-600">
          {t("pricing.eyebrow")}
        </span>
        <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-2 mb-3">
          {t("pricing.title")}
        </h2>
        <p className="text-slate-500">
          {t("pricing.subtitle")}
        </p>
      </div>

      <div className="relative max-w-7xl mx-auto">
        <button
          type="button"
          data-testid="carousel-prev"
          onClick={() => scrollCarousel(-1)}
          className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white border border-slate-200 shadow-lg items-center justify-center text-slate-700 hover:bg-slate-50 transition-colors"
          aria-label={t("pricing.prevAria")}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          type="button"
          data-testid="carousel-next"
          onClick={() => scrollCarousel(1)}
          className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white border border-slate-200 shadow-lg items-center justify-center text-slate-700 hover:bg-slate-50 transition-colors"
          aria-label={t("pricing.nextAria")}
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        <div
          ref={carouselRef}
          onScroll={handleCarouselScroll}
          data-testid="pricing-carousel"
          className="flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth pt-4 pb-6 px-1 items-stretch [scrollbar-width:thin]"
        >
          {plans.map((p, i) => {
            const meta = EDITORIAL_META[p.key] || {};
            const Icon = meta.icon || User;
            const ed = t(`pricing.editorial.${p.key}`, { returnObjects: true }) || {};
            const extras = normExtras(ed.extras);
            const features = buildFeatures(p, extras, t);
            const subprice = subpriceLabel(p, t);
            const showCalc = meta.calculator && p.included_members != null;
            return (
              <motion.div
                key={p.key}
                data-testid={`tier-${p.key}-card`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
                className={`relative rounded-2xl p-6 flex flex-col shrink-0 snap-start w-[85vw] sm:w-[420px] lg:w-[490px] ${
                  meta.highlight
                    ? "bg-emerald-500/[0.07] border-2 border-emerald-500/50 shadow-2xl shadow-emerald-500/10"
                    : "glass border border-slate-200"
                }`}
              >
                {ed.badge && (
                  <div
                    data-testid="popular-badge"
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-emerald-500 text-emerald-950 text-[11px] font-bold tracking-wide uppercase whitespace-nowrap"
                  >
                    {ed.badge}
                  </div>
                )}
                <div
                  className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${
                    meta.highlight
                      ? "bg-emerald-500/20 border border-emerald-500/40"
                      : "bg-slate-100 border border-slate-200"
                  }`}
                >
                  <Icon className={`w-5 h-5 ${meta.highlight ? "text-emerald-600" : "text-slate-600"}`} />
                </div>
                <h3 className="font-heading text-xl font-bold">{p.name}</h3>
                <div className="mt-1 mb-1 font-heading">
                  <span className="text-2xl font-extrabold" data-testid={`price-${p.key}`}>
                    {priceLabel(p, t)}
                  </span>
                  {periodLabel(p, t) && <span className="text-sm text-slate-400 font-medium">{periodLabel(p, t)}</span>}
                </div>
                {subprice && (
                  <div className="text-xs text-slate-500 mb-2" data-testid={`subprice-${p.key}`}>
                    {subprice}
                  </div>
                )}
                <p className="text-sm text-slate-500 leading-relaxed mb-5 min-h-[60px]">{ed.tagline}</p>

                {showCalc && (
                  <PriceCalculator
                    plan={p.key}
                    min={meta.calculator.min}
                    max={meta.calculator.max}
                    included={p.included_members}
                    highlight={meta.highlight}
                    region={activeRegion}
                  />
                )}

                <ul className="space-y-2.5 flex-1 mb-4">
                  {features.map((f, idx) => (
                    <Feature key={idx} item={f} />
                  ))}
                </ul>

                {ed.footnote && (
                  <div className="text-xs text-slate-500 border-t border-slate-200 pt-3 mb-4">
                    {ed.footnote}
                  </div>
                )}

                <Button
                  data-testid={`btn-choose-plan-${p.key}`}
                  onClick={() => choosePlan(p.key)}
                  className={
                    meta.highlight
                      ? "w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
                      : "w-full bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-900"
                  }
                >
                  {ed.cta}
                </Button>
              </motion.div>
            );
          })}
        </div>

        <div className="flex justify-center gap-2 mt-2" data-testid="carousel-dots">
          {plans.map((p, i) => (
            <button
              key={p.key}
              type="button"
              data-testid={`carousel-dot-${p.key}`}
              onClick={() => goToCard(i)}
              aria-label={t("pricing.goToAria", { name: p.name })}
              className={`h-2.5 rounded-full transition-all duration-300 ${
                activeIndex === i ? "w-7 bg-emerald-500" : "w-2.5 bg-slate-300 hover:bg-slate-400"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Comparatif détaillé */}
      <div className="max-w-7xl mx-auto mt-20">
        <h3 className="font-heading text-2xl font-bold text-center mb-8">{t("pricing.comparisonTitle")}</h3>
        <div className="overflow-x-auto rounded-2xl border border-slate-200" data-testid="comparison-table">
          <table className="w-full min-w-[720px] border-collapse">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left text-sm font-semibold text-slate-700 p-4 w-[24%]">{t("pricing.featureCol")}</th>
                {plans.map((p) => (
                  <th key={p.key} className="text-center text-sm font-semibold text-slate-700 p-4">
                    {p.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparison.map((group) => (
                <Fragment key={group.category}>
                  <tr className="bg-emerald-50/50">
                    <td
                      colSpan={plans.length + 1}
                      className="text-left text-xs font-bold uppercase tracking-wider text-emerald-700 px-4 py-2"
                    >
                      {group.category}
                    </td>
                  </tr>
                  {group.rows.map((row) => (
                    <tr key={row.label} className="border-t border-slate-100">
                      <td className="text-left text-sm text-slate-600 p-4">{row.label}</td>
                      {row.values.map((v, idx) => (
                        <td key={idx} className="text-center p-4">
                          <Cell value={v} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-400 text-center mt-4">
          {t("pricing.soonNote")}
        </p>
      </div>
    </section>
  );
};
